/**
 * Shared marketing motion primitives.
 *
 * The site previously ran a single FADE_UP on section entry and nothing else,
 * so past the first reveal every page read as static — no depth on hover, no
 * response to scrolling, nothing continuous. These primitives give the whole
 * marketing surface one consistent motion vocabulary.
 *
 * Rules that keep it feeling premium rather than gimmicky:
 *   - one easing curve everywhere (a soft "out-expo"), so nothing feels foreign
 *   - travel stays small (12–24px); motion reads as weight, not distance
 *   - reveals fire ONCE (`viewport.once`) — re-animating on scroll-back is the
 *     single most irritating pattern on marketing sites
 *   - every primitive collapses to a plain static element under
 *     `prefers-reduced-motion`, which is a real accessibility requirement:
 *     vestibular disorders make parallax and drift genuinely unpleasant
 */
import * as React from 'react';
import { motion, useReducedMotion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import type { Variants } from 'framer-motion';

/** Soft deceleration used by every marketing transition. */
export const EASE = [0.22, 1, 0.36, 1] as const;

export const REVEAL: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

/** Parent that walks its children in, one after another. */
export const STAGGER: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

/**
 * Reveal — fades/rises its children into view once, staggering any nested
 * `<Reveal.Item>`s. Drop it around any section.
 */
export function Reveal({
  children,
  className,
  /** Nudge when the reveal triggers relative to the viewport edge. */
  amount = 0.2,
  as = 'div',
}: {
  children: React.ReactNode;
  className?: string;
  amount?: number;
  as?: 'div' | 'section' | 'li' | 'ul';
}) {
  const reduced = useReducedMotion();
  const Comp = motion[as];

  if (reduced) {
    const Plain = as as React.ElementType;
    return <Plain className={className}>{children}</Plain>;
  }

  return (
    <Comp
      className={className}
      variants={STAGGER}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
    >
      {children}
    </Comp>
  );
}

/** A single staggered child of <Reveal>. */
export function RevealItem({
  children,
  className,
  as = 'div',
}: {
  children: React.ReactNode;
  className?: string;
  as?: 'div' | 'li' | 'p' | 'h2' | 'h3';
}) {
  const reduced = useReducedMotion();
  if (reduced) {
    const Plain = as as React.ElementType;
    return <Plain className={className}>{children}</Plain>;
  }
  const Comp = motion[as];
  return (
    <Comp className={className} variants={REVEAL}>
      {children}
    </Comp>
  );
}

/**
 * Parallax — drifts its child against the scroll direction as it crosses the
 * viewport. Deliberately gentle: `distance` is the total travel in px across
 * the whole crossing, so 40 means ±20px. Anything larger starts to look like
 * the layout is broken.
 */
export function Parallax({
  children,
  distance = 40,
  className,
}: {
  children: React.ReactNode;
  distance?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const raw = useTransform(scrollYProgress, [0, 1], [distance / 2, -distance / 2]);
  // Spring the output so a fast scroll wheel doesn't produce a jittery step.
  const y = useSpring(raw, { stiffness: 120, damping: 30, mass: 0.4 });

  if (reduced) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * CountUp — animates a number up once it scrolls into view.
 *
 * Takes the ALREADY-FORMATTED string (e.g. "$8,142.30") and re-formats each
 * frame from the parsed value, so currency symbols, separators and decimal
 * places survive exactly as authored. If the string has no parseable number it
 * renders unchanged rather than guessing.
 */
export function CountUp({ value, className }: { value: string; className?: string }) {
  const reduced = useReducedMotion();
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [shown, setShown] = React.useState<string>(() => value);

  // Split "$8,142.30" -> prefix "$", number 8142.30, suffix "".
  const parsed = React.useMemo(() => {
    const m = value.match(/^([^\d-]*)(-?[\d,]*\.?\d+)(.*)$/);
    if (!m) return null;
    const digits = m[2].replace(/,/g, '');
    const n = Number(digits);
    if (!Number.isFinite(n)) return null;
    const decimals = digits.includes('.') ? digits.split('.')[1].length : 0;
    const grouped = m[2].includes(',');
    return { prefix: m[1], suffix: m[3], target: n, decimals, grouped };
  }, [value]);

  React.useEffect(() => {
    if (!parsed || reduced || !inView) { setShown(value); return; }
    const DURATION = 900;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION);
      // Ease-out cubic: fast start, gentle settle.
      const eased = 1 - Math.pow(1 - t, 3);
      const current = parsed.target * eased;
      const body = current.toFixed(parsed.decimals);
      const [intPart, decPart] = body.split('.');
      const withSep = parsed.grouped ? Number(intPart).toLocaleString('en-US') : intPart;
      setShown(`${parsed.prefix}${withSep}${decPart ? '.' + decPart : ''}${parsed.suffix}`);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [parsed, reduced, inView, value]);

  return <span ref={ref} className={className}>{shown}</span>;
}
